"use client";

import { useEffect, useState } from "react";

import { getUser } from "@/lib/shop-agent-client";

import { useAuth } from "../contexts/AuthContext";

// Simple in-memory cache keyed by Firebase UID
const genderCache: Record<string, string> = {};

// Allow other parts of the app (e.g., onboarding) to prime the cache so
// first render after redirect has the correct gender immediately.
export function setCachedUserGender(userId: string, gender: string) {
  if (!userId) return;
  genderCache[userId] = gender;
}

export function useUserGender() {
  const { user, isAuthenticated } = useAuth();
  const [gender, setGender] = useState<string | null>(null);

  useEffect(() => {
    // Skip for logged-out or anonymous users - they don't have backend user records
    if (!user || !isAuthenticated) {
      setGender(null);
      return;
    }

    // If cached, use it immediately for the first paint
    if (genderCache[user.uid]) {
      setGender(genderCache[user.uid]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const userData = await getUser();
        const g = userData.gender || "unisex";
        genderCache[user.uid] = g;
        if (!cancelled) setGender(g);
      } catch (err) {
        console.error("Failed to load user gender", err);
        if (!cancelled) setGender("unisex");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, isAuthenticated]);

  return gender; // null while loading, otherwise mens / womens / unisex
}
